import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/client';
import { qk } from '@/lib/api/query-keys';

/**
 * 一個 `/api/v1/me*` 請求回 401 ＝ 工作階段已經不在了（過期、在別的裝置被撤銷、
 * 重設密碼登出了所有裝置）。那一刻就把「我是誰」設成 null：TopNav 換回「登入」、
 * 自選股頁換回登入提示，而不是讓每個元件各自把 401 畫成一個錯誤框。
 *
 * 只看 `/me*`：其他端點的 401 不代表工作階段狀態（它們大多不需要登入）。
 */
function sessionLost(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 401) return false;
  const path = new URL(error.url, 'http://localhost').pathname;
  return path === '/api/v1/me' || path.startsWith('/api/v1/me/');
}

/**
 * ARCHITECTURE §10.2。
 *
 * 這裡**不設**全域 staleTime：staleTime 是每一個查詢依 `as_of` 與 registry 的
 * `freq` 各自決定的（lib/api/stale.ts）。設一個全域預設值只會讓那個決定看起來
 * 可有可無，然後某個查詢就會忘了帶。
 *
 * `persistQueryClient` → IndexedDB（§10.2 規則 1）留在 P2 —— 它買的是
 * 「盤前 dashboard 在網路回來之前就從硬碟畫好」，而 P1 還沒有 dashboard。
 * 提早接上等於在沒有東西可以快取的時候先引入一個快取失效問題。
 */
export function createQueryClient(): QueryClient {
  const onError = (error: unknown) => {
    if (sessionLost(error)) queryClient.setQueryData(qk.me(), null);
  };
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        // 401 重試一次也還是 401：直接交給上面的 onError，不要多打一次。
        retry: (failureCount, error) =>
          failureCount < 1 && !(error instanceof ApiError && error.status === 401),
        refetchOnWindowFocus: false,
        // 資料沒有入場動畫，也不該有「重新整理」的閃動：保留舊資料直到新資料到齊。
        placeholderData: (previous: unknown) => previous,
      },
    },
  });
  return queryClient;
}
